import {
  effect,
  inject,
  linkedSignal,
  Resource,
  ResourceRef,
  Signal,
  signal,
  untracked,
  WritableResource,
} from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';

import { ErrorService } from './error.service';

/**
 * Returns the value of {@link resource}, or {@link defaultValue} while it has none.
 * Unlike `resource.value()`, this doesn't throw when the resource failed to load.
 */
export function valueOr<T, D>(resource: Resource<T | undefined>, defaultValue: D): T | D {
  return resource.hasValue() ? (resource.value() as T) : defaultValue;
}

/**
 * Like {@link valueOr}, but keeps the previous value while the resource loads new params (or fails to),
 * so that e.g. a list doesn't flash empty when going to the next page.
 */
export function keepPreviousValue<T, D>(resource: Resource<T | undefined>, defaultValue: D): Signal<T | D> {
  return linkedSignal<T | undefined, T | D>({
    source: () => valueOr(resource, undefined),
    computation: (value, previous) => value ?? previous?.value ?? defaultValue,
  });
}

/**
 * Shows an error dialog whenever {@link resource} fails to load, from which the user can retry loading it.
 * Must be called in an injection context.
 */
export function showErrorDialogOnFailure(resource: WritableResource<unknown>) {
  const errorService = inject(ErrorService);
  effect(() => {
    const error = resource.error();
    if (error) {
      untracked(() => errorService.handleError(error, () => resource.reload()));
    }
  });
}

export interface LazyResource<T> {
  resource: ResourceRef<T | undefined>;
  /**
   * Starts loading the resource if it wasn't loaded yet, or retries loading it when it failed.
   */
  load(): void;
}

/**
 * An rxResource that only starts loading once {@link LazyResource.load} is called.
 * Meant for data that is cached in a service, so it's only fetched the first time a component needs it.
 */
export function lazyRxResource<T>(stream: () => Observable<T>): LazyResource<T> {
  const requested = signal(false);
  const resource = rxResource({ params: () => requested() || undefined, stream });
  return {
    resource,
    load: () => {
      if (untracked(resource.error)) {
        resource.reload();
      } else {
        requested.set(true);
      }
    },
  };
}
